import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useCatalogLabels } from "@/i18n/catalog";
import { ArrowLeft, Star, Truck, ExternalLink, MessageCircle, Package } from "lucide-react";
import OrderDocumentsCard from "@/components/order/OrderDocumentsCard";
import { OrderContentDownloadBuyer } from "@/components/order/OrderContentDelivery";


export default function OrderDetailPage() {
  const { t, i18n } = useTranslation();
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [listing, setListing] = useState(null);
  const { addon: addonLabel } = useCatalogLabels();
  const [addonLabels, setAddonLabels] = useState({});
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/stilltouchedx/catalog/addons");
        const m = {}; for (const a of (data.addons || [])) m[a.code] = a.label;
        setAddonLabels(m);
      } catch { /* Fallback auf Roh-Code */ }
    })();
  }, []);
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);

  const STATUS_LABEL = {
    CREATED: t("order_detail.status_created"),
    PAYMENT_PENDING: t("order_detail.status_payment_pending"),
    PAID: t("order_detail.status_paid"),
    AWAITING_INBOUND: t("order_detail.status_awaiting_inbound"),
    INBOUND_RECEIVED: t("order_detail.status_inbound_received"),
    SHIPPED: t("order_detail.status_shipped"),
    DELIVERED: t("order_detail.status_delivered"),
    RECEIVED: t("order_detail.status_received"),
    COMPLETED: t("order_detail.status_completed"),
    CANCELLED: t("order_detail.status_cancelled"),
    REFUNDED: t("order_detail.status_refunded"),
  };

  // Rating-Widget State
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [showEdit, setShowEdit] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: o }, { data: r }] = await Promise.all([
          api.get(`/orders/${orderId}`),
          api.get(`/reviews/for-order/${orderId}`).catch(() => ({ data: { review: null } })),
        ]);
        setOrder(o);
        setReview(r.review);
        if (r.review) {
          setRating(r.review.rating);
          setText(r.review.text || "");
        }
        if (o.listing_id) {
          try {
            const { data: l } = await api.get(`/listings/${o.listing_id}`);
            setListing(l);
          } catch { /* deleted listing */ }
        }
      } catch { setOrder(null); }
      finally { setLoading(false); }
    })();
  }, [orderId]);

  const submit = async () => {
    if (rating < 1) { setSubmitError(t("order_detail.min_star")); return; }
    setSubmitting(true);
    setSubmitError("");
    try {
      if (review) {
        const { data } = await api.put(`/reviews/${review.review_id}`, { rating, text });
        setReview({ ...review, ...data.updated, rating, text });
        setShowEdit(false);
      } else {
        const { data } = await api.post("/reviews", { order_id: orderId, rating, text });
        setReview(data);
      }
    } catch (e) {
      setSubmitError(e.response?.data?.detail || t("order_detail.save_failed"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-full flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" />
      </div>
    );
  }
  if (!order) {
    return <div className="min-h-full px-4 py-16 text-center text-sm text-[#999]">{t("order_detail.not_found")}</div>;
  }

  // Frist-Fenster clientseitig grob berechnen (Server ist Wahrheit — 422 bei Ablauf)
  const refDate = order.received_at || order.delivered_at || order.shipped_at || order.created_at;
  const daysSinceRef = refDate ? Math.floor((Date.now() - new Date(refDate).getTime()) / (1000 * 60 * 60 * 24)) : null;
  const withinWindow = daysSinceRef !== null && daysSinceRef >= 0 && daysSinceRef < 14;
  const daysLeft = daysSinceRef !== null ? Math.max(0, 14 - daysSinceRef) : null;
  const canReview = withinWindow && ["DELIVERED", "RECEIVED", "COMPLETED"].includes(order.order_status || order.status);
  const displayRating = hoverRating || rating;

  return (
    <div data-testid="order-detail-page" className="min-h-full bg-white text-[#0A0A0A] pb-16">
      {/* Header */}
      <div className="sticky top-0 z-40 flex items-center gap-3 px-4 py-2.5 bg-white border-b border-[#E5E5E5]">
        <button data-testid="order-detail-back" onClick={() => navigate(-1)}
          className="w-9 h-9 -ml-1.5 flex items-center justify-center rounded-full hover:bg-[#F5F5F5]">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-bold leading-tight truncate">{t("order_detail.title")}</h1>
          <div className="flex items-center gap-1.5 mt-0.5">
            <p data-testid="order-detail-order-id" className="text-[10px] text-[#888] font-mono truncate">{order.order_id}</p>
            <button
              data-testid="order-detail-copy-id"
              onClick={() => {
                navigator.clipboard.writeText(order.order_id || "");
                setCopied(true);
                setTimeout(() => setCopied(false), 1200);
              }}
              className="text-[9px] px-1 py-0.5 border border-[#E5E5E5] text-[#666] hover:bg-[#F5F5F5] rounded shrink-0"
              title={t("order_detail.copy_title")}
            >
              {copied ? t("order_detail.copied") : t("order_detail.copy")}
            </button>
          </div>
        </div>
      </div>

      {/* Artikel-Karte */}
      <div className="px-4 py-4">
        <button
          data-testid="order-detail-listing-link"
          onClick={() => order.listing_id && listing && navigate(`/${listing.area_scope === "STILLTOUCHEDX" ? "stilltouchedx" : "listing"}/${order.listing_id}`)}
          disabled={!listing}
          className="w-full flex items-center gap-3 p-3 rounded-xl border border-[#E5E5E5] hover:border-[#0A0A0A] transition-colors text-left disabled:opacity-50">
          <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#F5F5F5] shrink-0">
            {listing?.media_urls?.[0] || listing?.title_photo_url ? (
              <img src={listing.title_photo_url || listing.media_urls[0]} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#CCC]">
                <Package size={22} />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">{order.title}</p>
            <p className="text-[10px] text-[#999] mt-0.5">
              {new Date(order.created_at).toLocaleDateString(i18n.language)} · {STATUS_LABEL[order.order_status] || order.order_status}
            </p>
            <p className="text-sm font-bold text-[#0A0A0A] mt-1">
              {Number(order.price || 0).toFixed(2)} {order.currency || "EUR"}
            </p>
          </div>
          {listing && <ExternalLink size={14} className="text-[#999] shrink-0" />}
        </button>
      </div>

      {/* iter270 · Vereinheitlichte Kaufübersicht (Optik wie Social/Abo) */}
      <div className="px-4 pb-4" data-testid="order-detail-overview">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#666] mb-1.5">{t("purchase_detail.overview", { defaultValue: "Kaufübersicht" })}</p>
        <div className="p-3 border border-[#E5E5E5] rounded-lg bg-white">
          <dl className="grid grid-cols-2 gap-y-2 text-sm">
            <dt className="text-[#666]">{t("purchase_detail.order_no", { defaultValue: "Bestellnummer" })}</dt>
            <dd className="text-right text-[#0A0A0A] font-mono text-xs">{order.public_order_no || order.order_id}</dd>
            <dt className="text-[#666]">{t("purchase_detail.payment_method", { defaultValue: "Zahlungsart" })}</dt>
            <dd className="text-right text-[#0A0A0A]">{order.payment_method || "—"}</dd>
            <dt className="text-[#666]">{t("purchase_detail.amount", { defaultValue: "Betrag" })}</dt>
            <dd className="text-right font-semibold text-[#0A0A0A]">{Number(order.price || order.total || 0).toFixed(2)} {order.currency || "EUR"}</dd>
          </dl>
        </div>
      </div>

      {/* Seller-Info */}
      {(order.seller_display_name || order.seller_handle) && (
        <div className="px-4 pb-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#666] mb-1.5">{t("order_detail.seller_label")}</p>
          <button
            data-testid="order-detail-seller-link"
            onClick={() => order.seller_handle && navigate(`/user/${order.seller_handle}`)}
            disabled={!order.seller_handle}
            className="text-sm font-medium text-[#0A0A0A] hover:text-[#666] transition-colors disabled:opacity-70">
            {order.seller_display_name || `@${order.seller_handle}`}
          </button>
        </div>
      )}

      {/* Extras aus Order */}
      {(Array.isArray(order.selected_addons) && order.selected_addons.length > 0) || order.selected_wear_days ? (
        <div className="px-4 pb-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#666] mb-1.5">{t("order_detail.extras")}</p>
          <ul className="text-xs text-[#666] space-y-0.5">
            {(order.selected_addons || []).map((a) => (
              <li key={a.code} data-testid={`order-detail-addon-${a.code}`} className="flex justify-between">
                <span data-testid={`order-detail-addon-label-${a.code}`}>{addonLabel(a.code, addonLabels[a.code] || a.code.replace(/^OPT_/, "").replaceAll("_", " ").toLowerCase())}</span>
                <span>+{Number(a.price).toFixed(2)} {order.currency || "EUR"}</span>
              </li>
            ))}
            {order.selected_wear_days && (
              <li data-testid={`order-detail-wear-${order.selected_wear_days.days}`} className="flex justify-between">
                <span>{order.selected_wear_days.days === 1 ? t("order_detail.days_extra_one", { n: 1 }) : t("order_detail.days_extra_other", { n: order.selected_wear_days.days })}</span>
                <span>+{Number(order.selected_wear_days.price).toFixed(2)} {order.currency || "EUR"}</span>
              </li>
            )}
          </ul>
        </div>
      ) : null}

      {/* Digitaler Content (Tragefotos/-videos) — einmaliger ZIP-Download */}
      {Array.isArray(order.selected_addons) && order.selected_addons.some(a => {
        const c = a.code || "";
        return c.startsWith("OPT_VIDEO_") || c.startsWith("OPT_PHOTOSET_");
      }) && (
        <OrderContentDownloadBuyer orderId={orderId} />
      )}

      {/* iter174 · Inline-Dokumente-Card (Käufer sieht seine Bestellübersicht) */}
      <OrderDocumentsCard orderId={orderId} />

      {/* Sonderwunsch (aus KI-Verhandlung / Presale) */}
      {order.special_request && (
        <div className="px-4 pb-4" data-testid="order-detail-special-request">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#666] mb-1.5">{t("order_detail.special_request_title")}</p>
          <div className="p-3 border border-amber-300 bg-amber-50 rounded-lg">
            <p className="text-sm text-amber-900 whitespace-pre-line">&bdquo;{order.special_request}&ldquo;</p>
            {order.negotiated_price != null && (
              <p className="text-[11px] text-amber-800 mt-1">{t("order_detail.special_request_price")} <strong>{Number(order.negotiated_price).toFixed(2)} {order.currency || "EUR"}</strong></p>
            )}
          </div>
        </div>
      )}

      {/* Status-Übersicht */}
      <div className="px-4 pb-4" data-testid="order-detail-status">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#666] mb-1.5">{t("order_detail.status_title")}</p>
        <div className="p-3 border border-[#E5E5E5] rounded-lg space-y-1.5 bg-white">
          <div className="flex items-center justify-between">
            <span className="text-xs text-[#666]">{t("order_detail.order_status_label")}</span>
            <span className="text-xs font-semibold text-[#0A0A0A]">{STATUS_LABEL[order.order_status || order.status] || order.order_status || order.status}</span>
          </div>
          {(() => {
            // Zahlung ableiten aus order.status (Legacy-Feld payment_status ist bei
            // manchen Alt-Bestellungen nie aktualisiert worden).
            const st = order.order_status || order.status;
            const isPostPaid = ["PAID","AWAITING_INBOUND","INBOUND_RECEIVED","SHIPPED","DELIVERED","RECEIVED","COMPLETED"].includes(st);
            const rawPayment = order.payment_status;
            const isPending = !isPostPaid && rawPayment === "PAYMENT_PENDING";
            const isFailed = rawPayment === "FAILED" || rawPayment === "REFUNDED";
            return (
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#666]">{t("order_detail.payment_label")}</span>
                <span className={`text-xs ${isFailed ? "text-red-600 font-semibold" : isPending ? "text-amber-700" : "text-emerald-700 font-medium"}`}>
                  {isFailed ? (rawPayment === "REFUNDED" ? t("order_detail.pay_refunded") : t("order_detail.pay_failed"))
                    : isPending ? t("order_detail.pay_pending")
                    : t("order_detail.pay_paid")}
                </span>
              </div>
            );
          })()}
          {order.shipping_mode && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#666]">{t("order_detail.shipping_mode")}</span>
              <span className="text-xs text-[#0A0A0A]">{order.shipping_mode === "ANONYMOUS_HUB" || order.shipping_mode === "HUB_ANONYMOUS" ? t("order_detail.shipping_hub") : t("order_detail.shipping_direct")}</span>
            </div>
          )}
          {order.shipped_at && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#666]">{t("order_detail.shipped_at")}</span>
              <span className="text-xs text-[#0A0A0A]">{new Date(order.shipped_at).toLocaleDateString(i18n.language)}</span>
            </div>
          )}
          {order.delivered_at && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#666]">{t("order_detail.delivered_at")}</span>
              <span className="text-xs text-[#0A0A0A]">{new Date(order.delivered_at).toLocaleDateString(i18n.language)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Versandstatus mit Progress-Steps */}
      <div className="px-4 pb-4" data-testid="order-detail-shipping-status">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#666] mb-1.5">{t("order_detail.ship_status_title")}</p>
        <div className="p-3 border border-[#E5E5E5] rounded-lg bg-white space-y-3">
          {(() => {
            const status = order.order_status || order.status;
            const isHub = ["ANONYMOUS_HUB", "HUB_ANONYMOUS"].includes(order.shipping_mode);
            const midLabel = isHub ? t("order_detail.step_processing") : t("order_detail.step_dispatching");
            const steps = [
              { key: "PAID", label: t("order_detail.status_paid"), done: ["PAID","AWAITING_INBOUND","INBOUND_RECEIVED","SHIPPED","DELIVERED","RECEIVED","COMPLETED"].includes(status), ts: order.paid_at || order.created_at },
              { key: "AWAITING_INBOUND", label: midLabel, done: ["AWAITING_INBOUND","INBOUND_RECEIVED","SHIPPED","DELIVERED","RECEIVED","COMPLETED"].includes(status), ts: null },
              { key: "SHIPPED", label: t("order_detail.status_shipped"), done: ["SHIPPED","DELIVERED","RECEIVED","COMPLETED"].includes(status), ts: order.shipped_at },
              { key: "DELIVERED", label: t("order_detail.status_delivered"), done: ["DELIVERED","RECEIVED","COMPLETED"].includes(status), ts: order.delivered_at },
            ];
            return (
              <div className="flex items-center justify-between gap-1">
                {steps.map((step, idx) => (
                  <div key={step.key} className="flex-1 min-w-0" data-testid={`shipping-step-${step.key}`}>
                    <div className="flex items-center gap-1">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${step.done ? "bg-emerald-600 text-white" : "bg-[#F5F5F5] text-[#CCC] border border-[#E5E5E5]"}`}>
                        {step.done ? "✓" : idx + 1}
                      </div>
                      {idx < steps.length - 1 && (
                        <div className={`flex-1 h-0.5 ${steps[idx + 1].done ? "bg-emerald-600" : "bg-[#E5E5E5]"}`} />
                      )}
                    </div>
                    <p className={`mt-1 text-[10px] leading-tight ${step.done ? "text-[#0A0A0A] font-medium" : "text-[#999]"}`}>{step.label}</p>
                    {step.ts && step.done && (
                      <p className="text-[9px] text-[#999]">{new Date(step.ts).toLocaleDateString(i18n.language)}</p>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Tracking-Info wenn vorhanden */}
          {(order.tracking_number || order.tracking_url || order.tracking_carrier) ? (
            <div className="pt-2 border-t border-[#F0F0F0] space-y-1">
              <p className="text-[10px] uppercase tracking-wider text-[#999] font-medium">{t("order_detail.tracking_title")}</p>
              <div className="flex flex-wrap gap-1.5 items-center">
                {order.tracking_carrier && (
                  <span data-testid="buyer-tracking-carrier" className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5] text-[10px] text-[#666]">
                    <Truck size={10} /> {order.tracking_carrier}
                  </span>
                )}
                {order.tracking_number && (
                  <span data-testid="buyer-tracking-number" className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5] text-[10px] text-[#666] font-mono">
                    {order.tracking_number}
                  </span>
                )}
                {order.tracking_url && (
                  <a href={order.tracking_url} target="_blank" rel="noopener noreferrer"
                    data-testid="order-detail-tracking-link"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-[#0A0A0A] text-white text-[10px] hover:bg-[#222]">
                    {t("order_detail.track_link")} <ExternalLink size={10} />
                  </a>
                )}
              </div>
            </div>
          ) : (order.order_status === "SHIPPED" || order.status === "SHIPPED") ? (
            <div className="pt-2 border-t border-[#F0F0F0]">
              <p className="text-[11px] text-[#999]">{t("order_detail.tracking_pending")}</p>
            </div>
          ) : null}

          {/* Kontextuelle Hinweise */}
          {(order.order_status === "PAID" || order.status === "PAID") && !order.shipped_at && (
            <p className="text-[11px] text-[#666] pt-2 border-t border-[#F0F0F0]">
              {t("order_detail.hint_paid_pending")}
            </p>
          )}
          {["AWAITING_INBOUND","INBOUND_RECEIVED"].includes(order.order_status || order.status) && (
            <p className="text-[11px] text-blue-700 pt-2 border-t border-[#F0F0F0]">
              {t("order_detail.hint_hub")}
            </p>
          )}
          {(order.order_status === "SHIPPED" || order.status === "SHIPPED") && !order.delivered_at && (
            <p className="text-[11px] text-emerald-700 pt-2 border-t border-[#F0F0F0]">
              {t("order_detail.hint_shipped")}
            </p>
          )}
        </div>
      </div>

      {/* Chat — ab Bezahlung bis 2 Wochen nach Kauf öffenbar (entkoppelt von Artikel-Verfügbarkeit) */}
      {["PAID", "AWAITING_SHIPMENT", "AWAITING_INBOUND", "INBOUND_RECEIVED", "SHIPPED", "DELIVERED", "RECEIVED", "COMPLETED"].includes(order.order_status || order.status) && (
        <div className="px-4 pb-4">
          <button
            data-testid="order-detail-chat"
            onClick={() => navigate(`/orders/${order.order_id}/chat`)}
            className="w-full h-11 rounded-lg border border-[#E5E5E5] text-sm text-[#0A0A0A] hover:bg-[#F5F5F5] transition-colors flex items-center justify-center gap-2">
            <MessageCircle size={14} /> {t("order_detail.chat_with_seller")}
          </button>
        </div>
      )}

      {/* Rating-Widget */}
      <div className="mx-4 mt-2 rounded-xl border border-[#E5E5E5] overflow-hidden" data-testid="order-detail-review-widget">
        <div className="px-4 py-3 bg-[#FAFAFA] border-b border-[#E5E5E5]">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#666]">
            {review ? t("order_detail.review_yours") : t("order_detail.review_submit")}
          </p>
          {daysLeft !== null && !review && canReview && (
            <p className="text-[10px] text-amber-600 mt-0.5">{daysLeft === 1 ? t("order_detail.review_days_left_one") : t("order_detail.review_days_left_other", { n: daysLeft })}</p>
          )}
          {!canReview && !review && (
            <p className="text-[10px] text-[#999] mt-0.5">
              {t("order_detail.review_not_yet")}
            </p>
          )}
        </div>

        {review && !showEdit ? (
          <div className="px-4 py-4 space-y-2">
            <div className="flex items-center gap-0.5" data-testid="order-detail-rating-display">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} size={20} className={i <= review.rating ? "text-amber-500 fill-amber-500" : "text-[#E5E5E5]"} />
              ))}
              <span className="ml-2 text-sm font-semibold text-[#0A0A0A] tabular-nums">{review.rating}/5</span>
            </div>
            {review.text && <p className="text-sm text-[#666] leading-relaxed">&bdquo;{review.text}&ldquo;</p>}
            <p className="text-[10px] text-[#999]">{t("order_detail.review_submitted_at", { date: new Date(review.created_at).toLocaleDateString(i18n.language) })}</p>
            {daysLeft !== null && daysLeft > 0 && (
              <button data-testid="order-detail-review-edit" onClick={() => setShowEdit(true)}
                className="text-[11px] text-[#0A0A0A] underline">{t("order_detail.review_edit")}</button>
            )}
          </div>
        ) : canReview || review ? (
          <div className="px-4 py-4 space-y-3">
            <div className="flex items-center justify-center gap-1" data-testid="order-detail-rating-input">
              {[1, 2, 3, 4, 5].map((i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`order-detail-star-${i}`}
                  onMouseEnter={() => setHoverRating(i)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setRating(i)}
                  className="p-1 transition-transform hover:scale-110">
                  <Star size={30} className={i <= displayRating ? "text-amber-500 fill-amber-500" : "text-[#E5E5E5]"} />
                </button>
              ))}
            </div>
            <textarea
              data-testid="order-detail-review-text"
              value={text} onChange={(e) => setText(e.target.value)}
              rows={3} maxLength={1000}
              placeholder={t("order_detail.review_placeholder")}
              className="w-full px-3 py-2 rounded-lg border border-[#E5E5E5] text-sm resize-none focus:outline-none focus:border-[#0A0A0A]" />
            {submitError && (
              <p data-testid="order-detail-review-error" className="text-[11px] text-red-600">{submitError}</p>
            )}
            <div className="flex gap-2">
              {showEdit && (
                <button onClick={() => { setShowEdit(false); setRating(review.rating); setText(review.text || ""); }}
                  className="flex-1 h-11 rounded-lg border border-[#E5E5E5] text-sm text-[#666] hover:bg-[#F5F5F5]">
                  {t("order_detail.cancel")}
                </button>
              )}
              <button data-testid="order-detail-review-submit" onClick={submit} disabled={submitting || rating < 1}
                className="flex-1 h-11 rounded-lg bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-[#333] transition-colors disabled:opacity-40">
                {submitting ? t("order_detail.saving") : (review ? t("order_detail.update") : t("order_detail.submit_review"))}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
